import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, first, map, startWith } from 'rxjs';
import { identity } from 'lodash';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { URLService } from './dynamic-url.service';
import { UserService } from './user.service';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let ga: any;

export enum FLAGS {
  PROFILES_PAGE = 'profiles_page',
  ENABLE_COMPREHENSION = 'enable_comprehension',
  ENABLE_REALTIME_RTM = 'enable_realtime_rtm',
  ENABLE_LESSONS = 'enable_lessons',
  USE_WHITEBOARD_FOR_P2P_CALLS = 'use_whiteboard_for_p2p_calls',
  TOPIC_RECOMMENDATIONS = 'topic_recommendations',
  TOPIC_BUILDER = 'topic_builder',
  DISPLAY_FILES_MENU = 'display_files_menu',
  CAPTURE_CAMERA_IMAGES = 'capture_camera_images',
  COURSE_MANAGEMENT = 'course_management',
  ENABLE_GROUP_MESSAGING = 'enable_group_messaging',
  REPORT_WB_BUG = 'report_wb_bug',
  WB_VIDEO_CHAT = 'wb_video_chat',
  WB_PDF_UPLOAD = 'wb_pdf_upload',
  WB_EQUATION_EDITOR = 'wb_equation_editor',
  WB_MESSAGING = 'wb_messaging',
  WB_IMAGE_UPLOAD = 'wb_image_upload',
  WB_VIDEO_UPLOAD = 'wb_video_upload',
  WB_BOARD_LOCK = 'wb_board_lock',
  WB_EXPORT = 'wb_export',
  WB_LEADER_MODE = 'wb_leader_mode',
  WB_GRID = 'wb_grid',
  WB_TEXT_BOX = 'wb_text_box',
  WB_SHAPES = 'wb_shapes',
  WB_GADGETS = 'wb_gadgets',
  WB_INVITE = 'wb_invite',
  WB_LEAVE_WHITEBOARD = 'wb_leave_whiteboard',
  WB_RECORD_SESSION = 'wb_record_session',
  WB_TABS = 'wb_tabs',
  WB_HIGHLIGHTER = 'wb_highlighter',
  WB_INVITE_TO_PENCIL = 'wb_invite_to_pencil',
  WB_INVITE_PARTICIPANTS = 'wb_invite_participants',
  WB_AVATAR = 'wb_avatar',
  WB_NAVIGATE_TO_SETTINGS = 'wb_navigate_to_settings',
  WB_UPLOAD_YOUTUBE = 'wb_upload_youtube',
  WB_NO_LONGPOLLING = 'wb_no_longpolling',
  WB_EMOTES = 'wb_emotes',
  WB_CREATE_BOARD_EACH_PARTICIPANT = 'wb_create_board_each_participant',
  WB_BOARD_PREVIEW_ONLY_VISIBLE = 'wb_board_preview_only_visible',
  WB_MAIN_TOOLBAR_V2_LEFT_SECTION = 'wb_main_toolbar_v2_left_section',
  WB_MAIN_TOOLBAR_V2_RIGHT_SECTION = 'wb_main_toolbar_v2_right_section',
  AUTHORING_DRAFTS = 'authoring_drafts',
  AUTHORING_TABLES = 'authoring_tables',
  SPACE_VIDEO_LOGGING = 'space_video_logging',
  ENABLE_PDF_UPSCALE = 'enable_pdf_upscale',
  ENABLE_BILLING = 'enable_billing',
  ENABLE_SPACE_AI_PRESENCE = 'enable_space_ai_presence',
  SPACES_LANGUAGE_SELECTOR = 'spaces_language_selector',
  USE_STAGED_INSTEAD_OF_UNSUBSCRIBE = 'use_staged_instead_of_unsubscribe',
  AUDIO_RELIABILITY_FIX = 'audio_reliability_fix',
  SPACES_TEXT_EDITOR = 'spaces_text_editor',
  SPACES_FREE_TIME_REFERRAL = 'spaces_free_time_referral',
  SPACES_SHOW_DEVICE_SELECTION_AFTER_CONNECTING = 'spaces_show_device_selection_after_connecting',
  SPACES_PARTICIPANTS_MANAGER = 'spaces_participants_manager',
  PERSISTENT_PERMISSIONS = 'persistent_permissions',
  ENABLE_VIDEO_POP_OUT = 'enable_video_pop_out',
  SPACES_LOCALIZED_LANGUAGES = 'spaces_localized_languages',
  SPACES_USER_CHIMES = 'spaces_user_chimes',
  SPACES_BOARD_DUPLICATE = 'spaces_board_duplicate',
  SPACES_KEEP_NEW_USERS_IN_SPACES_LIST = 'spaces_keep_new_users_in_spaces_list',
  SPACES_LANDING_PREVIEWS = 'spaces_landing_previews',
  ENABLE_SYNC_WITH_WB_SERVER = 'enable_sync_with_wb_server',
  SPACE_VIDEO_CALL_PROVIDER = 'cpaas_provider',
  LOGIN_REDESIGN = 'login_redesign',
  NOTIFICATIONS_BELL = 'notifications_bell',
  SPACES_DISABLE_INCOMING_VIDEOS = 'spaces_disable_incoming_videos',
  SPACES_LANDING_PAGE_NEW_UI = 'spaces_landing_page_new_ui',
  DEPLOYMENT_ID = 'deployment_id',
  WEB_URL = 'web_url',
  CALENDAR_ADDON_ENABLED = 'calendar_addon_enabled',
  SPACES_ANALYTICS_RECORDING = 'spaces_analytics_recording',
  SPACE_BINARY_CHUNKING = 'space_binary_chunking',
  SPACES_DIRECT_MESSAGING = 'spaces_direct_messaging',
  BREAKOUT_ROOMS = 'breakout_rooms',
  BREAKOUT_ROOMS_BOARDS = 'breakout_rooms_boards',
  SPACES_TILES_LETTERBOXING = 'spaces_tile_letterboxing',
  DYNAMIC_RIGHT_VIEW = 'dynamic_right_view',
  PROPAGATE_POINTER_LOCATIONS_ON_JOIN = 'propagate_pointer_locations_on_join',
  SPACES_TRANSCRIPTION = 'spaces_transcription',
  ENABLE_REDIS_PRESENCE = 'enable_redis_presence',
  WEBSOCKET_WEB_WORKER = 'websocket_web_worker',
  ENABLE_MOBILE_CHAT = 'enable_mobile_chat',
  FABRIC_OBJECT_CACHING = 'fabric_object_caching',
  CALL_TRACKS_V2 = 'call_tracks_v2',
  SPACES_DEV_TOOLS = 'spaces-dev-tools',
  SPACES_PRIVATE_BOARDS = 'spaces_private_boards',
  ENABLE_REPORTS_FILTERS = 'enable_reports_filters',

  ENABLE_SPACES_MATH_MANIPULATIVES = 'enable_spaces_math_manipulatives',
  ENABLE_SPACES_COMMENTS = 'enable_spaces_comments',
  SPACES_STICKY_NOTES = 'spaces_sticky_notes',
  SPACES_SWITCHER = 'spaces_switcher',
  MARIO = 'mario',
  CHAT_GPT = 'chat_gpt',
  MOBILE_ATTACHMENTS = 'mobile_attachments',
  SESSION_ANALYTICS_UI = 'session_analytics_ui',
  SPACES_REORDER_BOARDS = 'spaces_reorder_boards',
  SPACE_REALTIME_TEXT_UPDATE = 'space_realtime_text_update',
  SPACES_VIRTUAL_BACKGROUNDS = 'spaces_virtual_backgrounds',
  DEFAULT_VIRTUAL_BACKGROUNDS = 'default_virtual_backgrounds',
  SPACES_OBJECT_LOCK = 'spaces_object_lock',
  PICASSO = 'picasso',
  SPACES_TOOLBAR_SHORTCUTS = 'spaces_toolbar_shortcuts',
  GESTURES_WEB_WORKER = 'gestures_web_worker',
  ENABLE_FILE_PDF_CONVERSION = 'enable_file_pdf_conversion',
  SHOW_MULTI_TABS_WARNING = 'show_multi_tabs_warning',
  LOGIN_PROVIDERS = 'login_providers',
  ENABLE_DAILY_NOISE_CANCELLATION = 'enable_daily_noise_cancellation',
  SHOW_REFER_FRIEND = 'show_refer_friend',
  SPACES_BOARDS_BACKGROUNDS = 'spaces_boards_backgrounds',
  SPACES_BOARD_FOLDERS = 'spaces_board_folders',
  BOARD_CHANGE_CONFIRMATION_DIALOG = 'board_change_confirmation_dialog',
  TEST_CLONE_SPACES = 'test_clone_spaces',
  ENABLE_AUDIO_MUSIC_MODE = 'enable_audio_music_mode',
  ENABLE_CHATGPT = 'enable_chatgpt',
  ENABLE_TRANSCRIPTION_LOGGING = 'enable_transcription_logging',
  SPEECH_DURATION_COLUMN = 'speech_duration_column',
  APPS_PRIVACY_SWITCH = 'apps_privacy_switch',
  WEB_VIEWER_SCREENSHOT = 'web_viewer_screenshot',
  WEB_VIEWER_USE_CUSTOM_SEARCH_ENGINE = 'web_viewer_use_custom_search_engine',
  APPS_INTERACTIONS_LOCK_SWITCH = 'apps_interactions_lock_switch',
  APPS_AVAILABILITY_SWITCH = 'apps_availability_switch',
  CHAT_GPT_SCREENSHOT = 'chat_gpt_screenshot',
  SCREEN_SHARE_SCREENSHOT = 'screen_share_screenshot',
  PROJECT_SAM = 'project_sam',
  WEAK_NETWORK_DETECTION = 'weak_network_detection',
  PERFORMANCE_INTERVENTION_CPU = 'performance_intervention_cpu',
  PERFORMANCE_INTERVENTION_MEMORY = 'performance_intervention_memory',
  REDIS_FRAME_PRESENCE = 'redis_frame_presence',
  ENABLE_LOADING_TIPS = 'enable_loading_tips',
  SUPRESS_NOTIFICATIONS = 'supress_notifications',
  DISABLE_APPS_ON_MOBILE = 'disable_apps_on_mobile',
  SPACES_EXPERIENCE_RATING = 'spaces_experience_rating',
  ZOOMED_OUT_APPS_OVERLAY = 'zoomed_out_apps_overlay',
  SHOW_FORCE_CPU_TIERS = 'show_force_cpu_tiers',
  INACTIVITY_NOTIFICATIONS = 'inactivity_notifications',
  PROGRESS_INDICATOR_IMAGE_CANVAS = 'progress_indicator_image_canvas',
  SMALL_SCREEN_OVERLAY = 'small_screen_overlay',
  ONLY_ONE_WEBVIEWER_TIER_2 = 'only_one_webviewer_tier_2',
  DISABLE_RESOURCE_SIGNING = 'disable_resource_signing',
  ENABLE_CDN_RESOURCES_SIGNING = 'enable_cdn_resources_signing',
  LIMIT_FABRIC_OBJECTS_ON_BOARD = 'limit_fabric_objects_on_board',
  REQUEST_ACCESS_ENABLED = 'request_access_enabled',
  PENCIL_AI_ASSISTANT = 'pencil_ai_assistant',
  ENABLE_YJS_FABRIC_JSON = 'enable_yjs_fabric_json',
  ENABLE_ANONYMOUS_LOGIN = 'enable_anonymous_login',
  NOISE_CANCELLATION_DEFAULT_STATE = 'noise_cancellation_default_state',
  ENABLE_REDIS_USER_TEMPORARY_METADATA = 'enable_redis_user_temporary_metadata',
  LIVE_NOTIFICATION_SPEAKING_ASSISTANT = 'live_notification_speaking_assistant',
  LIVE_NOTIFICATION_SPEAKING_DURATION = 'live_notification_speaking_duration',
  ENABLE_SPACES_FILTERS = 'enable_space_filters',
  SPACE_AUTO_END_CALL = 'space_auto_end_call',
  ENABLE_MULTIPLE_JOIN_CALL_FROM_THE_SAME_ACCOUNT = 'enable_multiple_join_call_from_the_same_account',
  SCREEN_SHARE_SETTINGS = 'screen_share_settings',
  CHROME_CANVAS_BUG_FIX = 'chrome_canvas_bug_fix',
  ENABLE_DIRECT_CLIENT_DOWNLOAD_FROM_GCS = 'enable_direct_client_download_from_gcs',
  POINTER_EVENTS_FABRIC = 'pointer_events_fabric',
  ENABLE_INSTITUTION_BILLING_SETTINGS = 'enable_institution_billing_settings',
  ENABLE_AI_ASSISTANT = 'enable_ai_assistant',
  WHITEBOARD_URL = 'whiteboard_url',
  GALACTUS_URL = 'galactus_url',
  ENABLE_NETWORK_INDICATOR = 'enable_network_indicator',
  ENABLE_NSFW_DETECTION = 'enable_nsfw_detection',
  LOG_AUDIO_FREQUENCY_DATA = 'log_audio_frequency_data',
  CONTROL_RECORD_WHITEBOARD_API = 'control_record_whiteboard_api',
  SPACE_STICKY_SESSION = 'space_sticky_session',
  NEW_GALLERY_BAR_UI = 'new_gallery_bar_ui',
  FORCE_REFRESH_AFTER_INACTIVITY = 'force_refresh_after_inactivity',
  ENABLE_PORTAL = 'portal',
  ENABLE_CUSTOM_TEXTBOX_BEHAVIOR = 'enable_custom_textbox_behavior',
  CONSTANT_MEMORY_API_SPACE_SYNC_SETTINGS = 'constant_memory_api_space_sync_settings',
  ALLOW_NOISE_CANCELLATION_LOCAL_STATE = 'allow_noise_cancellation_local_state',
  BUFFER_FABRIC_UPDATE_ON_APPLY_UPDATE_LEVEL = 'buffer_fabric_update_on_apply_update_level',
  INSERT_APPS_IN_FREE_SPACES = 'insert_apps_in_free_spaces',
  ENABLE_AUTO_PICTURE_IN_PICTURE = 'enable_auto_picture_in_picture',
  EVENT_NEXT_SPACE = 'event_next_space',
  LIMIT_MISSING_UPDATE_SYNC = 'limit_missing_update_sync',
  ENABLE_FOLLOW_MODE = 'enable_follow_mode',
  IPAD_MULTI_TOUCH_HANDLING = 'ipad_multi_touch_handling',
  RENDER_IMAGES_IN_FABRIC = 'render_images_in_fabric',
  RENDER_PDF_AS_SEPARATE_IMAGES = 'render_pdf_as_separate_images',
  MIC_MUTE_DETECTION = 'mic_mute_detection',
  ENABLE_CLEAR_ALL_ANNOTATIONS = 'enable_clear_all_annotations',
  ENABLE_STICKY_NOTE_TEXT_STYLES = 'enable_sticky_note_text_styles',
  USE_SET_ENABLED_FOR_VIDEO_TRACKS = 'use_set_enabled_for_video_tracks',
  ENABLE_COMPUTE_PRESSURE_MEASUREMENT = 'enable_compute_pressure_measurement',
  VIDEO_TRACK_SETTINGS = 'video_track_settings',
  PAN_ZOOM_THROTTLE_TIME = 'pan_zoom_throttle_time',
  ENABLE_YSESSION_DIAGNOSTIC_LOGGING = 'enable_ysession_diagnostic_logging',
  CANVAS_ITEMS_KEYS_DELETION = 'canvas_items_keys_deletion',
  ENABLE_AGENT_JOIN_CALL = 'enable_agent_join_call',
  RENDER_PDFS_IN_FABRIC = 'render_pdfs_in_fabric',
  ENABLE_NEW_FONT_FAMILIES = 'enable_new_font_families',
  WEB_VIEWER_DELAYED_DESTROY = 'web_viewer_delayed_destroy',
  WEB_VIEWER_FILE_MANAGER = 'web_viewer_file_manager',
  HIDE_CALL_RECONNECTION_NOTIFICATION = 'hide_call_reconnection_notification',
  EMIT_EMBED_ANALYTICS = 'emit_embed_analytics',
  CHAT_DEBUG_LOGGING_ENABLED = 'chat_debug_logging_enabled',
  AI_RECORDING_INSIGHT_DASHBOARD = 'ai_recording_insight_dashboard',
  ANALYTICS_DASHBOARDS_V2 = 'analytics_dashboards_v2',
  RESYNC_ON_TIMEOUT = 'resync_on_timeout',
  BLOCK_UPDATES_DURING_SYNC = 'block_updates_during_sync',
  GALLERY_VIEW_PARAMETERS = 'gallery_view_parameters',
  ENABLE_JOIN_LEAVE_NOTIFICATIONS = 'enable_join_leave_notifications',
  INLINE_SVG_SPRITE_MAIN_DOC = 'inline_svg_sprite_main_doc',
  CANVAS_SCROLL_LIMIT = 'canvas_scroll_limit',
  ENABLE_IMAGE_LOADING_QUEUE = 'enable_image_loading_queue',
  ENABLE_RESIZE_IMAGES_EXTENSION = 'enable_resize_images_extension',
  ENABLE_IMAGES_CONVERSION = 'enable_images_conversion',
  ENABLE_BACKEND_APPS_LOADING = 'enable_backend_apps_loading',
  SETTINGS_TABS_VISIBILITY = 'settings_tabs_visibility',
  RECORD_WB_ON_CLOUD = 'record_wb_on_cloud',
  STORE_SCRIBBLES_AS_BUFFERS = 'store_scribbles_as_buffers',
  ENABLE_REFRESH_AFTER_UNRECOVERABLE_UPDATE_ERROR = 'enable_refresh_after_unrecoverable_update_error',
  ENABLE_RIGHT_VIEW_NEW_VIDEO_CONTROLS = 'enable_right_view_new_video_controls',
  ENABLE_BULK_UPLOAD_FLOW = 'enable_bulk_upload_flow',
  BYPASS_FLOWPORTALS_DOMAIN = 'bypass_flowportals_domain',
  ENABLE_ONE_ON_ONE_VIDEO_CALL_UI = 'enable_one_on_one_video_call_ui',
  VC_TILE_ORDERING = 'vc_tile_ordering',
  DELETE_LOCAL_RECORDING_AFTER_DOWNLOAD_ON_NEXT_OPEN = 'delete_local_recording_after_download_on_next_open',
  ENABLE_SCREEN_SHARE_ANNOTATIONS = 'enable_screen_share_annotations',
  ENABLE_BOARDS_PREVIEW_ON_TOUCH_DEVICES = 'enable_boards_preview_on_touch_devices',
  AI_SAFEGUARDING_UI = 'ai_safeguarding_ui',
  ENABLE_FULLSCREEN_APP_IN_CALL = 'enable_fullscreen_app_in_call',
  SPACES_OBSERVER_MODE = 'spaces_observer_mode',
  ENABLE_LASER_POINTER_TOOL = 'enable_laser_pointer_tool',
  ENABLE_TOUCH_DEVICE_DPR_FACTOR = 'enable_touch_device_dpr_factor',
  SKIP_APPS_OVERLAY_WHEN_LEADER_MODE_ENABLED = 'skip_apps_overlay_when_leader_mode_enabled',
  TOUCH_MINIMUM_ZOOM_NOTIFICATIONS = 'touch_minimum_zoom_notifications',
  ENABLE_NEW_SESSIONS_FLOW = 'enable_new_sessions_flow',
  ENABLE_LEGAL_AGREEMENTS_CONSENT = 'enable_legal_agreements_consent',
  OMNISEARCH = 'omnisearch',
}

// Per-dashboard toggles under ANALYTICS_DASHBOARDS_V2; the flag gates them all.
export enum AnalyticsDashboardV2Variable {
  SitesCoaching = 'sites_coaching',
  TeachersCoaching = 'teachers_coaching',
}

// OmniSearch's rollout switches under OMNISEARCH, the same ones the API gates on
// (pencil-api omnisearch_gate.ts): `serving` = search returns results, `summaries` =
// it also returns the AI answer.
export enum OmniSearchVariable {
  Serving = 'serving',
  Summaries = 'summaries',
}

const FLAGS_CSV_VARIABLES: { [key: string]: string[] } = {
  [FLAGS.MARIO]: ['enabled_mario_apps'],
  [FLAGS.ENABLE_LOADING_TIPS]: ['tips_items'],
};

@Injectable({
  providedIn: 'root',
})
/*
Provide access to current feature flags and experiments.
*/
export class FlagsService {
  featureFlags: Record<string, boolean> = {};
  featureFlagsVariables: Record<
    string,
    Record<string, string | number | boolean | Record<string, boolean>>
  > = {};
  customFeatureFlags?: Record<string, boolean>;
  featureFlagsChanged = new EventEmitter();
  isCustomFlagsEnabled = new BehaviorSubject(false);

  constructor(
    private userService: UserService,
    private urlService: URLService,
    private http: HttpClient,
    private authService: AuthService,
  ) {
    this.authService.logOutEmitter.subscribe(() => {
      this.getFlags();
    });
  }

  getFlags(): Promise<boolean> {
    return new Promise((resolve) => {
      const flagsJson = localStorage.getItem('feature-flags');
      this.isCustomFlagsEnabled.next(!!flagsJson);
      if (flagsJson) {
        try {
          // Ensure that if there are some new flags added and you have custom flags they show up
          // when you open the custom flags UI
          const defaultFlags = Object.values(FLAGS).reduce((prev, cur) => {
            prev[cur] = false;
            return prev;
          }, {});
          this.customFeatureFlags = { ...defaultFlags, ...JSON.parse(flagsJson) };
        } catch (ex) {
          this.customFeatureFlags = undefined;
          console.error(ex);
        }
      }

      this.userService.user.subscribe((user) => {
        if (user) {
          this.featureFlags = user?.user?.feature_flags ?? {};
          this.featureFlagsVariables =
            this.processSplitFlagCSVVariables(user?.user?.feature_flag_variables) ?? {};
          this.featureFlagsChanged.emit();
          // To set a custom diemnsion in GA, use the sendGTMData
          // method in app.component.ts to add new fields to the
          // GTM data layer, then gop to GTM to add the variables
          // and their mapping to custom diemnsions in GA.
          resolve(!!user);
        } else {
          this.getAnonymousFlags()
            .pipe(first())
            .subscribe({
              next: (res) => {
                if (res) {
                  this.featureFlags = res.flags;
                  this.featureFlagsVariables = this.processSplitFlagCSVVariables(res.flagVariables);
                  this.featureFlagsChanged.emit();
                  resolve(true);
                }
              },
              error: (res) => {
                console.log(res);
                resolve(false);
              },
            });
        }
      });
    });
  }
  /*
   * @emitInitSeparately: a flag to emit the default value whether
   * the value fetched from the API is the same or not
   */
  featureFlagChanged(flag: FLAGS, emitInitSeparately = false): Observable<boolean> {
    return this.featureFlagsChanged.asObservable().pipe(
      !emitInitSeparately ? startWith(() => this.isFlagEnabled(flag)) : identity,
      map(() => this.isFlagEnabled(flag)),
      distinctUntilChanged(),
      emitInitSeparately ? startWith(this.isFlagEnabled(flag)) : identity,
    );
  }

  isFlagEnabled(flag: FLAGS): boolean {
    const forced = (environment as unknown as { forcedFeatureFlags?: Record<string, boolean> })
      .forcedFeatureFlags?.[flag];
    if (forced !== undefined) {
      return forced;
    }
    const value = this.customFeatureFlags
      ? this.customFeatureFlags[flag]
      : this.featureFlags[flag] ?? false;
    return value;
  }

  isAnalyticsDashboardV2Enabled(variable: AnalyticsDashboardV2Variable): boolean {
    return (
      this.isFlagEnabled(FLAGS.ANALYTICS_DASHBOARDS_V2) &&
      !!this.featureFlagsVariables[FLAGS.ANALYTICS_DASHBOARDS_V2]?.[variable]
    );
  }

  /** Off unless the variable is explicitly true, as in the API, so a missing value hides it. */
  isOmniSearchEnabled(variable: OmniSearchVariable): boolean {
    return (
      this.isFlagEnabled(FLAGS.OMNISEARCH) &&
      this.featureFlagsVariables[FLAGS.OMNISEARCH]?.[variable] === true
    );
  }

  processSplitFlagCSVVariables(
    featureFlagsVariables: any,
  ): Record<string, Record<string, string | number | boolean | Record<string, boolean>>> {
    if (!featureFlagsVariables) {
      return {};
    }
    const outputFlags = JSON.parse(JSON.stringify(featureFlagsVariables));
    Object.keys(FLAGS_CSV_VARIABLES).forEach((key) => {
      if (featureFlagsVariables[key]) {
        FLAGS_CSV_VARIABLES[key].forEach((value) => {
          let splitRecord = {};
          if (featureFlagsVariables[key][value]) {
            splitRecord = (featureFlagsVariables[key][value] as string)
              .split(',')
              .reduce((recordMap, currentItem) => ({ ...recordMap, [currentItem]: true }), {});
          }
          outputFlags[key][value] = splitRecord;
        });
      }
    });
    return outputFlags;
  }

  /**
   * Overwrites the user feature flags with custom one.
   * This method uses local storage, so clearing cache will clear the custom flags
   * and revert back to using the original user feature flags coming from the BE.
   * @param flags The flags to overwrite
   */
  overwriteFlags(flags: Record<string, boolean>) {
    this.customFeatureFlags = Object.assign({}, flags);
    localStorage.setItem('feature-flags', JSON.stringify(this.customFeatureFlags));
  }

  /**
   * Resets the feature flags to the original user flags.
   */
  resetFlags() {
    this.customFeatureFlags = undefined;
    localStorage.removeItem('feature-flags');
  }

  getAnonymousFlags(): any {
    return this.http.get(`${this.urlService.getDynamicUrl()}/tutor/feature_flags`);
  }
}
